import { useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts'
import stringHelpers from '../../../utilities/stringHelpers'
import { AnalyticsSlider } from '../../shared/analyticsSlider'

const COLOUR = "#9F7AEA"

const percentile = (sorted, p) => sorted[Math.floor(sorted.length * p)]

const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null
    const value = payload[0].value
    return (
        <div className="analytics-tooltip">
            <p className="analytics-tooltip-title">{ label } g/km</p>
            <span className="analytics-tooltip-value">{ stringHelpers.formatDecimal(value, 0) } vehicle{ value !== 1 ? "s" : "" }</span>
        </div>
    )
}

export const CO2EmissionsChart = ({ analyticsData }) => {
    const [bucketSize, setBucketSize] = useState(10)
    const allValues = analyticsData.map(d => d.co2EmissionsGKm).filter(v => v != null && v !== 0 && v !== 999).sort((a, b) => a - b)

    if (allValues.length === 0) return null

    const rawMin = percentile(allValues, 0.02)
    const rawMax = percentile(allValues, 0.98)
    const co2Min = Math.floor(rawMin / bucketSize) * bucketSize
    const co2Max = Math.ceil(rawMax / bucketSize) * bucketSize

    const valid = analyticsData.filter(d => d.co2EmissionsGKm != null && d.co2EmissionsGKm !== 0 && d.co2EmissionsGKm !== 999)
    const belowMin = valid.filter(d => d.co2EmissionsGKm < co2Min).length
    const aboveMax = valid.filter(d => d.co2EmissionsGKm > co2Max).length

    const counts = valid
        .filter(d => d.co2EmissionsGKm >= co2Min && d.co2EmissionsGKm <= co2Max)
        .reduce((acc, d) => {
            const bucket = Math.floor(d.co2EmissionsGKm / bucketSize) * bucketSize
            acc[bucket] = (acc[bucket] ?? 0) + 1
            return acc
        }, {})

    const data = []
    if (belowMin > 0) {
        data.push({ name: `<${co2Min}`, value: belowMin })
    }
    for (let bucket = co2Min; bucket < co2Max; bucket += bucketSize) {
        data.push({ name: `${bucket}–${bucket + bucketSize}`, value: counts[bucket] ?? 0 })
    }
    if (aboveMax > 0) {
        data.push({ name: `>${co2Max}`, value: aboveMax })
    }

    return (
        <div className="analytics-chart-card">
            <div className="analytics-chart-card-header mb-1">
                <p className="analytics-chart-title">CO2 Emissions Distribution</p>
                <AnalyticsSlider label={ `${bucketSize} g/km intervals` } value={ bucketSize } min={ 5 } max={ 50 } step={ 5 } onChange={ setBucketSize } />
            </div>
            <p className="analytics-chart-subtitle mb-3">Vehicles by CO2 band (g/km)</p>
            <ResponsiveContainer width="100%" height={ 230 }>
                <BarChart data={ data } margin={ { top: 0, right: 8, bottom: 0, left: 0 } }>
                    <XAxis dataKey="name" tick={ { fontSize: 10, fill: "#4a5568" } } axisLine={ false } tickLine={ false } />
                    <YAxis tick={ { fontSize: 11, fill: "#a0aec0" } } axisLine={ false } tickLine={ false } allowDecimals={ false } />
                    <Tooltip content={ <CustomTooltip /> } cursor={ { fill: "#f7fafc" } } />
                    <Bar dataKey="value" radius={ [4, 4, 0, 0] } maxBarSize={ 40 } fill={ COLOUR } />
                </BarChart>
            </ResponsiveContainer>
        </div>
    )
}
